import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from '../components/DateRangePicker';
import InspectorHoverCard from '../components/InspectorHoverCard';
import { useAuth } from '../contexts/AuthContext';

interface LineInspectionRecord {
  id: string;
  basis: string;
  ref_number: string;
  defect_count: number;
  dateTime: string;
  rawDate: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  result: 'PASS' | 'FAIL' | 'PENDING';
  qaExecutive: {
    name: string;
    initials: string;
    color: string;
    photo?: string;
    inspectorId: string | null;
  };
}

const LineInspectionReports: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [reports, setReports] = useState<LineInspectionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All Reports');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState('All');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;


  useEffect(() => {
    const fetchReports = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/line-inspections`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.status === 401) {
          logout();
          return;
        }
        const data = await response.json();
        if (data.success) {
          const mapped: LineInspectionRecord[] = data.data.map((r: any) => ({
            id: r.id,
            basis: r.basis || 'N/A',
            ref_number: r.ref_number || 'N/A',
            defect_count: r.defect_count || 0,
            dateTime: new Date(r.created_at).toLocaleString('en-GB', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            }),
            rawDate: r.created_at,
            status: r.status,
            result: r.result,
            qaExecutive: {
              name: r.inspector?.name || r.inspector_name || 'Unknown',
              initials: getInitials(r.inspector?.name || r.inspector_name || 'Unknown'),
              color: getAvatarColor(r.inspector?.id || 'default'),
              photo: r.inspector?.profileImage || undefined,
              inspectorId: r.inspector?.id || null,
            },
          }));
          setReports(mapped);
        }
      } catch (err) {
        console.error('Failed to fetch line reports:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
        fetchReports();
    }
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const uniqueUserObjects = [
    { name: 'All', photo: undefined, color: '', initials: '' },
    ...(Array.from(new Map(reports.map((r: LineInspectionRecord) => [r.qaExecutive.name, r.qaExecutive])).values())),
  ];

  const filteredReports = reports.filter(r => {
    const reportStatus = r.status === 'COMPLETED' ? (r.result === 'PASS' ? 'Passed' : 'Failed') : 'In Progress';
    const matchesStatus = activeFilter === 'All Reports' || reportStatus === activeFilter;
    const matchesUser = selectedUser === 'All' || r.qaExecutive.name === selectedUser;
    
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || 
      r.id.toLowerCase().includes(q) || 
      r.ref_number.toLowerCase().includes(q) ||
      r.qaExecutive.name.toLowerCase().includes(q);

    const reportDate = new Date(r.rawDate);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    const matchesDate = (!from || reportDate >= from) && (!to || reportDate <= to);

    return matchesStatus && matchesUser && matchesSearch && matchesDate;
  });


  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter, selectedUser, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedReports = filteredReports.slice(indexOfFirstItem, indexOfLastItem);


  const getStatusBadge = (report: LineInspectionRecord) => {
    if (report.status !== 'COMPLETED') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 whitespace-nowrap">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span>
          In Progress
        </span>
      );
    }

    if (report.result === 'PASS') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 whitespace-nowrap">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
          Passed
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 whitespace-nowrap">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500"></span>
        Failed
      </span>
    );
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = [
      'bg-blue-100 text-blue-600',
      'bg-emerald-100 text-emerald-600',
      'bg-purple-100 text-purple-600',
      'bg-amber-100 text-amber-600',
      'bg-rose-100 text-rose-600',
      'bg-indigo-100 text-indigo-600',
      'bg-slate-200 text-slate-600'
    ];
    const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  return (
    <div className="mx-auto w-full max-w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Line Inspection Reports</h1>
        <p className="text-sm text-slate-500">Manage and review inspection outcomes</p>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center">
          {/* Search Box */}
          <div className="relative w-full md:max-w-md">
            <input
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm placeholder-slate-400 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] outline-none"
              placeholder="Search by Ref Number..."
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-[20px] text-slate-400">search</span>
          </div>

          {/* User Filter */}
          <div className="relative inline-block" ref={userDropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer min-w-[140px]"
            >
              {selectedUser === 'All' ? (
                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                </span>
              ) : (() => {
                const u = uniqueUserObjects.find(x => x.name === selectedUser);
                return u?.photo ? (
                  <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                ) : (
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u?.color || 'bg-slate-200 text-slate-600'}`}>
                    {u?.initials || selectedUser[0]}
                  </span>
                );
              })()}
              <span className="truncate">{selectedUser === 'All' ? 'All Users' : selectedUser}</span>
              <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
            </button>
            {isUserDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 z-20 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                {uniqueUserObjects.map(u => (
                  <button
                    key={u.name}
                    onClick={() => { setSelectedUser(u.name); setIsUserDropdownOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedUser === u.name ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                  >
                    {u.name === 'All' ? (
                      <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                      </span>
                    ) : u.photo ? (
                      <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                    ) : (
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u.color}`}>
                        {u.initials}
                      </span>
                    )}
                    {u.name === 'All' ? 'All Users' : u.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Date Picker */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-medium text-slate-700">Filter Date:</span>
          <DateRangePicker 
            range={{ from: dateFrom, to: dateTo }}
            onChange={(range) => {
              setDateFrom(range.from);
              setDateTo(range.to);
            }}
            onClear={() => {
              setDateFrom('');
              setDateTo('');
            }}
          />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {['All Reports', 'Passed', 'Failed', 'Pending'].map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all shadow-sm ${
              activeFilter === filter
                ? 'bg-[#136bce] text-white shadow-blue-500/30'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-6 py-4 font-semibold tracking-wide">Inspection ID</th>
                <th className="px-6 py-4 font-semibold tracking-wide">PO/Batch/Shift</th>
                <th className="px-6 py-4 font-semibold tracking-wide">Ref Number</th>
                <th className="px-6 py-4 font-semibold tracking-wide">QA Executive</th>
                <th className="px-6 py-4 font-semibold tracking-wide">Total Defects</th>
                <th className="px-6 py-4 font-semibold tracking-wide">DATE & TIME</th>
                <th className="px-6 py-4 font-semibold tracking-wide">Status</th>
                <th className="px-6 py-4 text-right font-semibold tracking-wide"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                   <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#136bce]"></div>
                      Loading inspection reports...
                    </div>
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    No inspection reports found.
                  </td>
                </tr>
              ) : (
                paginatedReports.map((report) => (
                  <tr
                    key={report.id}
                    className="group cursor-pointer transition-colors hover:bg-slate-50"
                    onClick={() => navigate(`/line-inspection-report/${report.id}`)}
                  >

                    <td className="px-6 py-4 text-slate-500 font-mono text-xs">{report.id.substring(0, 8).toUpperCase()}</td>
                    <td className="px-6 py-4 font-medium text-slate-900">{report.basis}</td>
                    <td className="px-6 py-4">{report.ref_number}</td>
                    <td className="px-6 py-4">
                      <InspectorHoverCard
                        inspectorId={report.qaExecutive.inspectorId}
                        name={report.qaExecutive.name}
                        initials={report.qaExecutive.initials}
                        color={report.qaExecutive.color}
                        photo={report.qaExecutive.photo}
                        token={token || ''}
                      >
                        {report.qaExecutive.photo ? (
                          <img
                            src={report.qaExecutive.photo}
                            alt={report.qaExecutive.name}
                            className="h-7 w-7 rounded-full object-cover border border-slate-200"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const sibling = target.nextElementSibling as HTMLElement;
                              if (sibling) sibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold ${report.qaExecutive.color}`}
                          style={{ display: report.qaExecutive.photo ? 'none' : 'flex' }}
                        >
                          {report.qaExecutive.initials}
                        </div>
                        {report.qaExecutive.name}
                      </InspectorHoverCard>
                    </td>
                    <td className="px-6 py-4">{report.defect_count}</td>
                    <td className="px-6 py-4">{report.dateTime}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(report)}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="material-symbols-outlined text-slate-400 opacity-0 transition-opacity group-hover:opacity-100">
                        chevron_right
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredReports.length)}</span> of <span className="font-medium">{filteredReports.length}</span> results
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show first, last, and pages around current
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => (
                <React.Fragment key={page}>
                  {index > 0 && array[index - 1] !== page - 1 && (
                    <div className="flex h-8 w-8 items-center justify-center text-slate-400">...</div>
                  )}
                  <button
                    onClick={() => setCurrentPage(page)}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? "bg-[#136bce] text-white shadow-sm ring-1 ring-[#136bce]"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    {page}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LineInspectionReports;
